import {Dialog, DialogActions, DialogContent, DialogTitle, Grid} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Field, Form, Formik} from 'formik';
import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import * as Yup from 'yup';
import FormButton from '../../../base-components/StudioButton/FormButton';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {toast} from '../../../base-components/StudioToast';
import STUDIO_URL from '../../../config/url';
import {
  setIsAddDeviceModalOpen,
  setStreamingDevices,
  TASK_STATUS,
  useDeploy,
  INFERENCE_SOURCE_TEXT,
} from '../useDeploy';
import {useSelector} from 'react-redux';
import md5 from 'md5';
import {FileExplorer} from '../FileExplorer';
import {convertFileSize} from '../../../utils';
import {TestRegionSelection} from '../TestRegionSelection';
import {fetchUserToken} from '../../SettingsView/UserDetails';
import {DropArea} from '../../../base-components/StudioDropArea';
import {includesProtocolPrefix} from '../../../utils';

import './AddStreamingDeviceForm.scss';
import {
  EVENT_TYPE,
  VideoStream,
  VideoStreamController,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../HlsVideoStream';
import {FileVideoStream} from '../HlsVideoStream/FileVideoStream';

const convertPointsToRelative = (origWidth, origHeight, points) => {
  return points
    ? points.map(point => ({
        x: point.x / origWidth,
        y: point.y / origHeight,
      }))
    : [];
};

const convertBoxToPoints = (origWidth, origHeight, data) => {
  // top left corner
  const r1X = data.x / origWidth;
  const r1Y = data.y / origHeight;
  // lower right corner
  const r2X = (data.x + data.width) / origWidth;
  const r2Y = (data.y + data.height) / origHeight;
  return [
    {x: r1X, y: r1Y},
    {x: r2X, y: r1Y},
    {x: r2X, y: r2Y},
    {x: r1X, y: r1Y},
  ];
};

const AddStreamingBaseDeviceForm = ({
  className = '',
  onAddedAsset,
  userId,
  enableSelect = false,
}) => {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [restartConfig, setRestartConfig] = useState();
  const [previewConfig, setPreviewConfig] = useState();
  const [previewStatus, setPreviewStatus] = useState();
  const [videoMetadata, setVideoMetadata] = useState();
  const [device, setDevice] = useState();
  const [localVideoUrl, setLocalVideoUrl] = useState('');
  const [regions, setRegions] = useState();
  const [{streamingDevices, isAddDeviceModalOpen, deployConfig}, dispatch] = useDeploy();
  const [streamSubject, setStreamSubject] = useState();
  const [videoController, setVideoController] = useState();
  const [streamReady, setStreamReady] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);

  const serverConfig = useSelector(state => state.config);
  const [existingDevice, setExistingDevice] = useState(null);

  const MAX_FILE_UPLOAD_MB = useMemo(
    () => convertFileSize(serverConfig?.maxVideoUploadSize, 'b', 'mb'),
    [serverConfig?.maxVideoUploadSize]
  );

  const sourceOptions = {
    IP_CAMERA: INFERENCE_SOURCE_TEXT.get('IP_CAMERA'),
    FILE: INFERENCE_SOURCE_TEXT.get('FILE'),
  };

  class PreviewStreamObserver extends VideoStreamObserver {
    handleEvent(event) {
      switch (event.type) {
        case EVENT_TYPE.CONNECTING:
          setPreviewStatus(TASK_STATUS.READY);
          break;
        case EVENT_TYPE.CONNECTED:
          setStreamConnected(true);
          break;
        case EVENT_TYPE.CONNECTION_FAILED:
          setStreamConnected(false);
          setStreamReady(false);
          break;
        case EVENT_TYPE.REMOTE_STREAM:
          setPreviewStatus(TASK_STATUS.CONNECTED);
          setStreamReady(true);
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          setPreviewStatus(TASK_STATUS.COMPLETE);
          setStreamReady(false);
          break;
        case EVENT_TYPE.ERROR:
          setPreviewStatus(TASK_STATUS.ERROR);
          if (event.message) {
            console.error(event.message);
          }
          toast.error(event.message);
          setStreamReady(false);
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    setLocalVideoUrl('');
    setStreamSubject(new VideoStreamSubject());
  }, []);

  useEffect(() => {
    setPreviewConfig(restartConfig);
  }, [restartConfig]);

  useEffect(() => {
    const [dimWidth, dimHeight] = [800, 480];
    let selectedDevice = streamingDevices.find(x => x.id === deployConfig.ipCamera);
    setDevice(selectedDevice);

    const amendSelection = (x, i) => {
      const pnts = x.points.map(({x, y}) => {
        return {x: x * dimWidth, y: y * dimHeight};
      });
      let label = x.name || '';
      label = /^(Line|Region)\s+\d+$/.test(label) ? null : label;
      const defLbl = (x?.points?.length === 2 ? 'Line ' : 'Region ') + i;

      const defaultObject = {
        id: (x?.points?.length === 2 ? 'LINE_' : 'POLY_') + i,
        label: label || defLbl,
        complete: true,
      };
      return {...defaultObject, ...x, points: pnts};
    };

    if (isAddDeviceModalOpen) {
      setIsSubmitting(false);
      if (selectedDevice && !previewConfig?.url) {
        restartPreview({
          source: 'IP_CAMERA',
          url: selectedDevice.url,
        });
        setRegions({
          lines: selectedDevice.lines?.map(amendSelection),
          polygons: selectedDevice.regions?.map(amendSelection),
        });
        setExistingDevice(selectedDevice);
      }
    }
  }, [deployConfig.ipCamera, isAddDeviceModalOpen, previewConfig?.url, streamingDevices]);

  useEffect(
    function startPreviewStream() {
      if (previewConfig && previewConfig.url && previewConfig.source && streamSubject) {
        streamSubject.addListener('PreviewStreamObserver', new PreviewStreamObserver());
        const controller = new VideoStreamController();
        controller.setVideoUnavailableMessageKey('stream.video.url.unavailable.short');

        controller.start({
          limitedDuration: false,
          source: previewConfig.source,
          url: `${serverConfig.hlsServerUrl}/${md5(previewConfig.url)}/index.m3u8`,
          sourceUrl: previewConfig.url,
          streamModel: 'PROXIED_PUSH',
          serverConfig,
          monitorConfig: {
            pollingInterval: 3,
            logOutput: true,
            monitorStream: true,
          },
          streamSubject,
        });
        setVideoController(controller);

        return () => controller.stop();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewConfig, serverConfig]
  );

  useEffect(
    function stopStreamIfModalClosed() {
      if (!isAddDeviceModalOpen && videoController) {
        videoController.stop();
      }
    },
    [isAddDeviceModalOpen, videoController]
  );

  const uploadFile = async uploadedFile => {
    try {
      let token = await fetchUserToken();
      const buffer = await uploadedFile.arrayBuffer();
      const response = await axios.put(STUDIO_URL.UPLOAD_VIDEO_FILEMAN, buffer, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/octet-stream',
          Authorization: `Basic ${userId}:${token}`,
        },
      });
      return response.data;
    } catch (e) {
      toast.error(intl.formatMessage({id: 'stream.videoFileUpload.uploadError'}));
      console.error(e);
    }
  };

  const packageRegion = (region, isBox = false) => {
    if (region == null) {
      return [];
    }
    return region.map(el => {
      return {
        name: el.label,
        points: isBox
          ? convertBoxToPoints(
              videoMetadata ? videoMetadata.clientWidth : 800,
              videoMetadata ? videoMetadata.clientHeight : 480,
              el
            )
          : convertPointsToRelative(
              videoMetadata ? videoMetadata.clientWidth : 800,
              videoMetadata ? videoMetadata.clientHeight : 480,
              el.points
            ),
      };
    });
  };

  const addNewDevice = async (name, source, url) => {
    try {
      const amend = existingDevice != null;
      const {data} = await axios.post(
        amend ? STUDIO_URL.DEVICE_UPD : STUDIO_URL.DEVICE_NEW,
        {
          id: existingDevice?.id || null,
          name,
          type: source,
          url,
          lines: packageRegion(regions?.lines),
          regions: [
            ...packageRegion(regions?.polygons),
            // convert box labels to Point coordinates
            ...packageRegion(regions?.boxes, true),
          ],
        }
      );
      if (data.body) {
        const device = data.body;
        const devices = streamingDevices.filter(x => x.id !== device.id);
        dispatch(setStreamingDevices([...devices, device]));
        const msg = amend ? 'added' : 'updated';
        toast.success(`Successfully ${msg} streaming input: ${name}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async values => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      const {name, source} = values;
      let url;

      if (source === 'IP_CAMERA') {
        url = values.url;
      } else if (source === 'FILE') {
        const {uploadedFile} = values;

        url = await uploadFile(uploadedFile);
      }

      if (url) {
        await addNewDevice(name, source, url);
        dispatch(setIsAddDeviceModalOpen(false));
        setLocalVideoUrl('');
        restartPreview(null);
        setExistingDevice(null);
        setRegions({lines: [], polygons: []});

        onAddedAsset && onAddedAsset(name, url);
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setLocalVideoUrl('');
    restartPreview(null);
    setExistingDevice(null);
    setRegions({lines: [], polygons: []});
    dispatch(setIsAddDeviceModalOpen(false));
  };

  const handleVideoLoadedMetadata = metadata => {
    setVideoMetadata(metadata);
  };

  const restartPreview = newPreviewConfig => {
    setStreamReady(false);
    setStreamConnected(false);
    setVideoController(undefined);
    setRestartConfig(newPreviewConfig);
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(intl.formatMessage({id: 'stream.nameError'})),
    url: Yup.string().when('source', {
      is: 'IP_CAMERA',
      then: Yup.string()
        .required(intl.formatMessage({id: 'stream.urlRequired'}))
        .test('url-test', intl.formatMessage({id: 'stream.urlInvalid'}), value => {
          try {
            new URL(value);
            return true;
          } catch (e) {
            return false;
          }
        })
        .test(
          'valid-protocol-prefix',
          intl.formatMessage({
            id: 'test.addStreamingDevice.invalidUrlPrefix',
          }),
          value => includesProtocolPrefix(value)
        ),
    }),
    uploadedFile: Yup.mixed().when('source', {
      is: 'FILE',
      then: Yup.mixed().required(intl.formatMessage({id: 'stream.fileRequired'})),
    }),
  });

  return (
    <Dialog
      open={isAddDeviceModalOpen}
      className={`${className} add-streaming-device`}
      fullWidth
      maxWidth="xl"
    >
      <Formik
        initialValues={{
          name: device ? device.name : '',
          source: deployConfig.inferenceSource,
          uploadedFile: null,
          url: device ? device.url : '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({values, isValid, handleReset}) => (
          <Form className="add-streaming-device__form">
            <DialogTitle
              disableTypography={true}
              className="project-form-container__header"
            >
              <h4 className="project-form-container__header__title">
                <FormattedMessage id="stream.configure" />
              </h4>
              <span
                className="project-form-container__header__close"
                onClick={() => {
                  handleReset();
                  handleCancel();
                }}
              >
                <Close />
              </span>
            </DialogTitle>
            <DialogContent className="add-streaming-device__form-body">
              <Grid container={true}>
                <Grid xs={12} lg={3} item={true}>
                  <Field name="name">
                    {({field, meta}) => (
                      <StudioTextField
                        required
                        name="name"
                        className="add-streaming-device__field"
                        label="Name"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && !!meta.error ? meta.error : ''}
                        {...field}
                      />
                    )}
                  </Field>
                  {enableSelect ? (
                    <Field name="source">
                      {({field, meta}) => (
                        <StudioSelect
                          label={
                            <span className="add-streaming-device__field__label">
                              {intl.formatMessage({
                                id: 'test.addStreamingDeviceTypeLabel',
                              })}
                            </span>
                          }
                          className="add-streaming-device__field"
                          options={sourceOptions}
                          error={meta.touched && meta.error}
                          helperText={meta.touched && meta.error ? meta.error : ''}
                          SelectProps={{...field}}
                        />
                      )}
                    </Field>
                  ) : (
                    <Field name="source">
                      {({field, meta}) => (
                        <StudioTextField
                          {...field}
                          disabled
                          error={meta.touched && meta.error}
                          value={sourceOptions[field.value]}
                          className="add-streaming-device__field"
                          helperText={meta.touched && meta.error ? meta.error : ''}
                          label={
                            <span className="add-streaming-device__field__label">
                              {intl.formatMessage({
                                id: 'test.addStreamingDeviceTypeLabel',
                              })}
                            </span>
                          }
                        />
                      )}
                    </Field>
                  )}
                  <Field name="url">
                    {({field, meta, form}) =>
                      form.values.source === 'IP_CAMERA' && (
                        <>
                          <StudioTextField
                            required
                            label="URL"
                            className="add-streaming-device__field"
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && !!meta.error ? meta.error : ''}
                            {...field}
                            onChange={(...args) => {
                              restartPreview(null);
                              field.onChange(...args);
                            }}
                            onBlur={(...args) => {
                              if (!meta.error) {
                                if (!previewConfig || previewConfig.url !== values.url) {
                                  restartPreview({
                                    source: values.source,
                                    url: values.url,
                                  });
                                }
                              }
                              field.onBlur(...args);
                            }}
                          />
                          <p className="add-streaming-device__helper_text">
                            <FormattedMessage id="stream.supportedProtocols" />
                          </p>
                        </>
                      )
                    }
                  </Field>
                </Grid>
                <Grid
                  xs={12}
                  lg={9}
                  item={true}
                  className="add-streaming-device__preview_container"
                >
                  <Field name="url">
                    {({field, meta, form}) =>
                      form.values.source === 'IP_CAMERA' && (
                        <div className="add-streaming-device__preview">
                          {(!previewConfig || meta.error) && <video />}
                          {previewConfig?.url && !meta.error && previewConfig.url && (
                            <div className="add-streaming-device__video_container">
                              <TestRegionSelection
                                key={`test-region-selection-${previewConfig.url}`}
                                regions={regions}
                                onChange={setRegions}
                                disabled={isSubmitting}
                                status={
                                  previewStatus !== TASK_STATUS.CONNECTED
                                    ? previewStatus
                                    : null
                                }
                                source={
                                  <VideoStream
                                    hlsRef={videoController?.hlsClient.hlsRef}
                                    streamReady={streamReady}
                                    streamConnected={streamConnected}
                                    monitor={videoController?.monitor}
                                    onLoadedMetadata={handleVideoLoadedMetadata}
                                  />
                                }
                              />
                            </div>
                          )}
                          {previewConfig?.html && !meta.error && (
                            <div
                              className="add-streaming-device__iframe-preview"
                              dangerouslySetInnerHTML={{__html: previewConfig.html}}
                            />
                          )}
                        </div>
                      )
                    }
                  </Field>
                  <Field name="uploadedFile">
                    {({field, form}) =>
                      form.values.source === 'FILE' && (
                        <>
                          <div className="add-streaming-device__field">
                            <DropArea
                              accept="video/*"
                              maxSize={serverConfig?.maxVideoUploadSize}
                              onDropRejected={() => {
                                toast.error(
                                  intl.formatMessage(
                                    {
                                      id: 'stream.videoFileUpload.error',
                                    },
                                    {
                                      size: `${MAX_FILE_UPLOAD_MB}MB`,
                                    }
                                  )
                                );
                              }}
                              message={intl.formatMessage(
                                {
                                  id: 'stream.videoFileUpload',
                                },
                                {
                                  size: MAX_FILE_UPLOAD_MB,
                                }
                              )}
                              onChange={files => {
                                const file = files[0];

                                form.setFieldValue('uploadedFile', file);
                                setLocalVideoUrl(file ? URL.createObjectURL(file) : '');
                              }}
                            />
                            <FileExplorer
                              files={field.value ? [field.value] : []}
                              onDelete={() => {
                                setLocalVideoUrl('');
                                form.setFieldValue('uploadedFile', null);
                              }}
                              onClear={() => {
                                setLocalVideoUrl('');
                                form.setFieldValue('uploadedFile', null);
                              }}
                              size="small"
                              icon={isSubmitting ? 'check' : 'delete'}
                            />
                          </div>
                          {localVideoUrl && (
                            <TestRegionSelection
                              regions={regions}
                              onChange={setRegions}
                              disabled={isSubmitting}
                              onReset={() => {
                                setLocalVideoUrl('');
                                setRegions({lines: [], polygons: []});
                                form.setFieldValue('uploadedFile', null);
                              }}
                              source={
                                <FileVideoStream
                                  localVideoUrl={localVideoUrl}
                                  onLoadedMetadata={handleVideoLoadedMetadata}
                                />
                              }
                            />
                          )}
                        </>
                      )
                    }
                  </Field>
                  {/* TODO: Add later  */}
                  {/* <SupportedVideoTypes /> */}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className="add-streaming-device__actions">
              {streamSubject && (
                <FormButton
                  type="button"
                  buttonRole="secondary"
                  value="Cancel"
                  disabled={isSubmitting}
                  onClick={() => {
                    handleReset();
                    handleCancel();
                  }}
                />
              )}
              <FormButton
                type="submit"
                buttonRole="primary"
                value="Submit"
                disabled={!isValid || isSubmitting} // This has been commented and I did not find the reason why
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  userId: state.user.userId,
});

export const AddStreamingDeviceForm = connect(mapStateToProps)(
  AddStreamingBaseDeviceForm
);

PropTypes.AddStreamingDeviceForm = {
  className: PropTypes.string,
  enableSelect: PropTypes.bool,
  onAddedAsset: PropTypes.func.isRequired,
};
